import React from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { AD_IMAGE_ASPECT, AD_IMAGE_HEIGHT, AD_IMAGE_WIDTH } from "../../lib/ads";
import { cropImageToFile } from "../../lib/imageCrop";

export const AdImageCropDialog = ({
  open,
  imageSrc,
  sourceFileName,
  sourceMimeType,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [cropPixels, setCropPixels] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropPixels(null);
      setBusy(false);
      setError("");
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!imageSrc || !cropPixels || busy) return;
    setBusy(true);
    setError("");
    try {
      const croppedFile = await cropImageToFile({
        imageSrc,
        cropPixels,
        outputWidth: AD_IMAGE_WIDTH,
        outputHeight: AD_IMAGE_HEIGHT,
        fileName: sourceFileName,
        mimeType: sourceMimeType,
      });
      await onConfirm(croppedFile);
    } catch (err) {
      setError(err?.message || "Cropping failed. Please try again.");
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onCancel?.()}>
      <DialogContent className="max-w-3xl border-cyan-300/40 bg-slate-950 text-slate-100" data-testid="ad-image-crop-dialog">
        <DialogHeader>
          <DialogTitle data-testid="ad-image-crop-title">Crop ad image</DialogTitle>
          <DialogDescription className="text-slate-300" data-testid="ad-image-crop-description">
            Drag to position the crop. Final output will be {AD_IMAGE_WIDTH} × {AD_IMAGE_HEIGHT}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="relative overflow-hidden rounded-2xl border border-cyan-300/30 bg-black/60"
            style={{ height: 360 }}
            data-testid="ad-image-crop-canvas"
          >
            {imageSrc ? (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={AD_IMAGE_ASPECT}
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setCropPixels(areaPixels)}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-300" data-testid="ad-image-crop-no-image">
                No image selected.
              </div>
            )}
          </div>

          <div className="space-y-2" data-testid="ad-image-crop-zoom-control-wrap">
            <label className="text-xs text-slate-300" htmlFor="ad-image-crop-zoom-control">Zoom</label>
            <input
              id="ad-image-crop-zoom-control"
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-cyan-400"
              data-testid="ad-image-crop-zoom-control"
            />
          </div>

          {error ? <div className="text-sm text-rose-300" data-testid="ad-image-crop-error">{error}</div> : null}

          <div className="flex justify-end gap-2" data-testid="ad-image-crop-actions">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded-full border border-slate-600 px-4 py-2 text-sm"
              data-testid="ad-image-crop-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || !imageSrc || !cropPixels}
              className="rounded-full border border-cyan-300/50 bg-cyan-500/15 px-4 py-2 text-sm font-semibold text-cyan-100"
              data-testid="ad-image-crop-confirm"
            >
              {busy ? "Applying..." : "Use cropped image"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};